/**
 * BuildLogsModal
 *
 * Wide modal that fetches and displays build logs for a given build
 * (custom app build history). Shows container logs, component logs,
 * or database fallback.
 *
 * When a build is just being initialized (status pending/queued or the
 * pipeline run does not exist yet → 404), no error is shown: the modal
 * displays a "Build initialized — logs will appear shortly" wait state
 * and silently retries the fetch every few seconds.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Loader2, RefreshCw } from 'lucide-react';
import Modal from '../Common/Modal';
import forgeService from '../../services/forgeService';
import type { ForgeApplicationDetails, BuildInfo } from '../../types';
import type { BuildLogsResponse } from '../../types';

interface BuildLogsModalProps {
  open: boolean;
  onClose: () => void;
  application: ForgeApplicationDetails | null;
  build: BuildInfo | null;
}

/** Build statuses where the pipeline run may not exist yet → expect 404 */
const PRE_RUN_STATUSES = new Set(['pending', 'queued', 'creating', 'initializing']);

/** Active states that justify retrying the logs fetch in the background */
const ACTIVE_STATUSES = new Set([
  'pending',
  'queued',
  'creating',
  'initializing',
  'cloning',
  'building',
  'running',
  'pushing',
  'deploying',
]);

/**
 * Build pipeline step keywords in execution order. A container/step name is
 * ranked by the first keyword it contains, so tabs are presented in the order
 * the build actually runs (clone → build → push → deploy) rather than the
 * arbitrary order the API returns them in.
 */
const STEP_ORDER_KEYWORDS = [
  'init',
  'prepare',
  'clone',
  'fetch',
  'checkout',
  'source',
  'generate',
  'config',
  'render',
  'lint',
  'test',
  'build',
  'compile',
  'kaniko',
  'image',
  'push',
  'scan',
  'sign',
  'deploy',
  'apply',
  'sync',
  'rollout',
];

/** Rank a container/step name by its position in the build pipeline. */
function stepRank(containerName: string): number {
  const name = (containerName || '').toLowerCase();
  for (let i = 0; i < STEP_ORDER_KEYWORDS.length; i++) {
    if (name.includes(STEP_ORDER_KEYWORDS[i])) return i;
  }
  return STEP_ORDER_KEYWORDS.length;
}

/** Rank used for database logs so they sort after all build steps. */
const DB_STEP_RANK = STEP_ORDER_KEYWORDS.length + 1;

/** Tab label: container name only (e.g. "step-clone"), no pod name or line count */
function shortLogLabel(containerName: string): string {
  return containerName || 'logs';
}

function LogContent({ lines }: { lines: string[] }) {
  const text = lines.length ? lines.join('\n') : '(no lines)';
  return (
    <pre className="p-3 text-xs font-mono text-foreground overflow-auto h-full min-h-0 whitespace-pre-wrap break-words bg-muted/20 rounded-b-lg">
      {text}
    </pre>
  );
}

type LogTabItem = {
  id: string;
  shortLabel: string;
  lines: string[];
  /** Component grouping (0 for single-component / non-component builds). */
  componentIndex: number;
  /** Pipeline-step rank used for ordering within a component. */
  stepRank: number;
  /** Original insertion order, used as a stable tie-breaker. */
  seq: number;
  /** 1-based position shown as a prefix in the tab label. */
  number: number;
};

/**
 * Flatten response into tab items: one tab per container (short name) or one
 * for database logs. Tabs are ordered by component, then by build-step order
 * (clone → build → push → deploy), and numbered 1, 2, 3… so the UI reflects
 * the real sequence of the build operation.
 */
function buildLogTabs(data: BuildLogsResponse): LogTabItem[] {
  const tabs: Omit<LogTabItem, 'number'>[] = [];
  const seen = new Set<string>();
  let seq = 0;

  if (data.components && data.components.length > 0) {
    data.components.forEach((comp, componentIndex) => {
      for (const c of comp.logs ?? []) {
        const id = `${c.pod_name}-${c.container_name}`;
        if (!seen.has(id)) {
          seen.add(id);
          tabs.push({
            id,
            shortLabel: shortLogLabel(c.container_name),
            lines: c.log_lines ?? [],
            componentIndex,
            stepRank: stepRank(c.container_name),
            seq: seq++,
          });
        }
      }
      if (comp.database_logs) {
        const id = `db-${comp.build_id}`;
        if (!seen.has(id)) {
          seen.add(id);
          tabs.push({
            id,
            shortLabel: comp.component_name ? `db (${comp.component_name})` : 'db',
            lines: comp.database_logs.split('\n'),
            componentIndex,
            stepRank: DB_STEP_RANK,
            seq: seq++,
          });
        }
      }
    });
  } else {
    for (const c of data.logs ?? []) {
      tabs.push({
        id: `${c.pod_name}-${c.container_name}`,
        shortLabel: shortLogLabel(c.container_name),
        lines: c.log_lines ?? [],
        componentIndex: 0,
        stepRank: stepRank(c.container_name),
        seq: seq++,
      });
    }
    if (data.database_logs) {
      tabs.push({
        id: 'database',
        shortLabel: 'db',
        lines: data.database_logs.split('\n'),
        componentIndex: 0,
        stepRank: DB_STEP_RANK,
        seq: seq++,
      });
    }
  }

  tabs.sort(
    (a, b) =>
      a.componentIndex - b.componentIndex ||
      a.stepRank - b.stepRank ||
      a.seq - b.seq,
  );

  return tabs.map((tab, idx) => ({ ...tab, number: idx + 1 }));
}

export const BuildLogsModal: React.FC<BuildLogsModalProps> = ({
  open,
  onClose,
  application,
  build,
}) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<BuildLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  // True while we are waiting for the pipeline run to exist (pending build → 404).
  // We use this to render a "Build initialized" placeholder instead of an error.
  const [waitingForRun, setWaitingForRun] = useState(false);

  /**
   * Fetch the build logs. `background` keeps the currently displayed logs and
   * active tab in place (used for auto-refresh polling and the manual refresh
   * button), whereas a foreground fetch shows the full loading state.
   */
  const fetchLogs = useCallback(
    async (background: boolean) => {
      if (!application || !build) return;
      const status = (build.status || '').toLowerCase();
      const expectMissing = PRE_RUN_STATUSES.has(status);

      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await forgeService.getApplicationBuildLogs(
          application.client_id,
          application.forge_id,
          application.app_id,
          { build_id: build.build_id, tail_lines: 500 },
        );
        setData(res);
        setError(null);
        setWaitingForRun(false);
        setLastUpdated(Date.now());
      } catch (err: unknown) {
        const e = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
        const httpStatus = e.response?.status;
        const isMissing = httpStatus === 404;

        // Pipeline run not created yet (or build still pending): the auto-refresh
        // poll keeps retrying; show the friendly "build initialized" placeholder.
        if (isMissing && (expectMissing || ACTIVE_STATUSES.has(status))) {
          setData(null);
          setError(null);
          setWaitingForRun(true);
          return;
        }

        // On a background refresh keep whatever we already have rather than
        // replacing live logs with an error flash.
        if (!background) {
          setData(null);
          setWaitingForRun(false);
          setError(
            e.response?.data?.detail ||
              e.message ||
              t('forge.application.buildLogsError', 'Failed to load build logs'),
          );
        }
      } finally {
        if (background) setRefreshing(false);
        else setLoading(false);
      }
    },
    [
      application?.client_id,
      application?.forge_id,
      application?.app_id,
      build?.build_id,
      build?.status,
      t,
    ],
  );

  // Initial load + reset whenever the modal opens or the target build changes.
  useEffect(() => {
    if (!open || !application || !build) {
      setData(null);
      setError(null);
      setWaitingForRun(false);
      setLastUpdated(null);
      return;
    }
    setActiveTabIndex(0);
    fetchLogs(false);
  }, [open, fetchLogs]);

  // Auto-refresh: while the modal is open, silently re-fetch logs on an
  // interval (also drives the retry while waiting for the pipeline run).
  useEffect(() => {
    if (!open || !application || !build) return;
    const id = setInterval(() => fetchLogs(true), 4000);
    return () => clearInterval(id);
  }, [open, fetchLogs]);

  const appName = application?.name ?? '';
  const statusPart = build?.status ? ` (${build.status})` : '';
  const title = [t('forge.application.buildLogsTitle', 'Build Logs'), appName].filter(Boolean).join(' — ') + statusPart;

  const refreshButton = (
    <button
      type="button"
      onClick={() => fetchLogs(true)}
      disabled={refreshing}
      title={t('forge.application.refreshLogs', 'Refresh')}
      className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg px-2.5 py-1 disabled:opacity-60"
    >
      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
      <span className="hidden sm:inline">{t('forge.application.refreshLogs', 'Refresh')}</span>
    </button>
  );

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      headerActions={refreshButton}
      className="max-w-6xl w-full mx-4 h-[85vh] max-h-[90vh] flex flex-col overflow-hidden"
      bodyClassName="p-0 flex flex-col min-h-0 flex-1 overflow-hidden"
      hideDefaultFooter
    >
      <div className="flex flex-col min-h-0 flex-1 overflow-hidden p-4">
        {/* Safe-to-close banner */}
        <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-primary/10 border border-primary/20 text-sm text-primary shrink-0">
          <Info className="h-4 w-4 flex-shrink-0" />
          {t('forge.application.buildSafeClose', 'You can safely close this window. The build will continue in the background.')}
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">{t('forge.application.loadingLogs', 'Loading logs…')}</p>
        )}
        {!loading && waitingForRun && (
          <div className="flex flex-col items-center justify-center text-center gap-3 py-12">
            <Loader2 className="h-6 w-6 text-primary animate-spin" />
            <div>
              <p className="text-sm font-medium text-foreground">
                {t('forge.application.buildInitialized', 'Build initialized')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  'forge.application.buildInitializedHint',
                  'The build pipeline is starting — logs will appear here as soon as they are available.',
                )}
              </p>
            </div>
          </div>
        )}
        {!loading && !waitingForRun && error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        {!loading && !waitingForRun && !error && data && (() => {
          const tabs = buildLogTabs(data);
          const activeTab = tabs[activeTabIndex];
          return (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3 shrink-0">
                <span>{data.source}</span>
                {data.total_lines != null && data.total_lines > 0 && (
                  <span> · {data.total_lines} lines</span>
                )}
                {data.status && (
                  <span className="capitalize"> · {data.status}</span>
                )}
                {lastUpdated && (
                  <span className="ml-auto flex items-center gap-1 text-xs">
                    {refreshing && <Loader2 className="h-3 w-3 animate-spin" />}
                    {t('forge.application.logsUpdatedAt', 'Updated at {{time}}', {
                      time: new Date(lastUpdated).toLocaleTimeString(i18n.language),
                    })}
                  </span>
                )}
              </div>
              {data.error_message && (
                <p className="text-sm text-destructive mb-3 shrink-0">{data.error_message}</p>
              )}
              {tabs.length === 0 ? (
                <p className="text-sm text-muted-foreground shrink-0">{t('forge.application.noLogs', 'No logs available')}</p>
              ) : (
                <>
                  <div className="flex flex-wrap border-b border-border gap-1 shrink-0">
                    {tabs.map((tab, idx) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTabIndex(idx)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                          idx === activeTabIndex
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span className="mr-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold text-muted-foreground">
                          {tab.number}
                        </span>
                        {tab.shortLabel}
                      </button>
                    ))}
                  </div>
                  <div className="min-h-0 flex-1 mt-0 overflow-hidden">
                    {activeTab && (
                      <LogContent lines={activeTab.lines} />
                    )}
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>
      <div className="px-4 py-3 border-t border-border/60 flex justify-end shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5"
        >
          {t('global.close')}
        </button>
      </div>
    </Modal>
  );
};

export default BuildLogsModal;
